import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Users, Mail, Lock, User, ArrowRight, KeyRound, Heart
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SecondParentJoinPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        join_code: '',
        email: '',
        password: '',
        confirmPassword: '',
        name: ''
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (formData.join_code.length !== 8) {
            setError('Join code should be 8 characters');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${BACKEND_URL}/api/family/join`, {
                join_code: formData.join_code.toUpperCase(),
                email: formData.email,
                password: formData.password,
                name: formData.name
            });

            localStorage.setItem('token', response.data.access_token);
            navigate('/family/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to join family. Please check your join code.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="noise-overlay" />
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <Card className="glass-card">
                    <CardContent className="p-8">
                        {/* Header */}
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <Heart className="w-8 h-8 text-primary" />
                            </div>
                            <h1 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                Join Your Family
                            </h1>
                            <p className="text-muted-foreground text-sm">
                                Enter the join code from your partner to access the family dashboard
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="join_code">Join Code</Label>
                                <div className="relative">
                                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="join_code"
                                        data-testid="join-code-input"
                                        type="text"
                                        placeholder="ABCD1234"
                                        value={formData.join_code}
                                        onChange={(e) => setFormData({...formData, join_code: e.target.value.toUpperCase()})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50 font-mono text-center uppercase tracking-widest"
                                        maxLength={8}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="name">Your Name</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="name"
                                        data-testid="second-parent-name"
                                        type="text"
                                        placeholder="Your name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        data-testid="second-parent-email"
                                        type="email"
                                        placeholder="you@example.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        data-testid="second-parent-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.password}
                                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                        minLength={6}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmPassword">Confirm Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="confirmPassword"
                                        data-testid="second-parent-confirm-password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="join-error">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                data-testid="join-family-btn"
                                className="w-full h-12 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                                disabled={loading}
                            >
                                {loading ? 'Joining...' : 'Join Family'}
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                        </form>

                        {/* Links */}
                        <div className="mt-6 text-center text-sm">
                            <p className="text-muted-foreground">
                                Don&apos;t have a join code?{' '}
                                <Link to="/family/pricing" className="text-primary font-medium hover:underline">
                                    Start a Family Plan
                                </Link>
                            </p>
                            <p className="text-muted-foreground mt-2">
                                Already have an account?{' '}
                                <Link to="/login" className="text-primary font-medium hover:underline">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default SecondParentJoinPage;
